import { FC, useState, useEffect, useRef, memo } from "react";
import { Button, Input, Skeleton, Tooltip, Tag } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { AiOutlineUser } from "react-icons/ai";
import { ChatThreadDisplay } from "./hook";

const statusClassNameMap: Record<string, string> = {
  新規: "new",
  対応中: "in-progress",
  完了: "done",
};

// Memoized ChatThreadItem component to prevent unnecessary re-renders
type ChatThreadItemProps = {
  thread: ChatThreadDisplay;
  onClick: () => void;
  isUpdated?: boolean;
  statusOptions: any[];
};

/* eslint-disable react/prop-types */
const ChatThreadItem = memo<ChatThreadItemProps>(
  ({ thread, onClick, isUpdated = false, statusOptions }) => {
    const [isHighlighted, setIsHighlighted] = useState(false);
    const [showTooltip, setShowTooltip] = useState(false);
    const prevIsUpdatedRef = useRef(isUpdated);

    useEffect(() => {
      // Only trigger animation when isUpdated changes from false to true
      // Not when it stays true or changes from true to false
      if (isUpdated && !prevIsUpdatedRef.current) {
        setIsHighlighted(true);
        // Remove highlight after animation completes
        const timer = setTimeout(() => {
          setIsHighlighted(false);
        }, 2000);
        prevIsUpdatedRef.current = isUpdated;
        return () => clearTimeout(timer);
      } else {
        prevIsUpdatedRef.current = isUpdated;
        // Reset highlight if isUpdated becomes false
        if (!isUpdated) {
          setIsHighlighted(false);
        }
      }
    }, [isUpdated]);

    const handleMouseEnter = (e: React.MouseEvent<HTMLSpanElement>) => {
      const target = e.currentTarget;
      if (target.scrollWidth > target.clientWidth) {
        setShowTooltip(true);
      } else {
        setShowTooltip(false);
      }
    };

    const getStatusColor = (statusId: string | number | undefined) => {
      if (!statusId) return null;
      const status = statusOptions.find(
        (s) => String(s.value) === String(statusId),
      );
      return status?.color;
    };

    const hexToRgba = (hex: string, alpha = 0.08) => {
      if (!hex || !hex.startsWith("#")) return "";
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };

    const statusColor = getStatusColor(thread.statusId);

    return (
      <button
        type="button"
        className={`chat-list__item ${
          isHighlighted ? "chat-list__item--updated" : ""
        } ${thread.unreadCount > 0 ? "chat-list__item--unread" : ""}`}
        onClick={onClick}
      >
        <div className="chat-list__item-header">
          <p className="chat-list__item-id-title">
            <span>#{thread.id}</span>
            <span className="chat-list__item-title-wrapper">
              <Tooltip title={showTooltip ? thread.subject : ""}>
                <span
                  className="chat-list__item-title"
                  onMouseEnter={handleMouseEnter}
                >
                  {thread.subject}
                </span>
              </Tooltip>
            </span>
          </p>
        </div>
        <div className="chat-list__item-meta">
          <div className="chat-list__item-user-info">
            <span className="chat-list__item-user-wrapper">
              <AiOutlineUser className="chat-list__item-icon" />
              <span className="chat-list__item-requester">
                {thread.requester}
              </span>
            </span>
            {thread.companyName && (
              <span className="chat-list__item-company">
                {thread.companyName}
              </span>
            )}
            <p className="chat-list__item-time">
              <span>作成: </span>
              <span>{thread.createdAt}</span>
            </p>
          </div>
        </div>

        {thread.status && (
          <Tag
            className={`chat-list__item-status ${
              !statusColor
                ? `chat-list__item-status--${
                    statusClassNameMap[thread.status] || ""
                  }`
                : ""
            }`}
            style={
              statusColor
                ? {
                    color: statusColor,
                    backgroundColor: hexToRgba(statusColor, 0.08),
                    border: `1px solid ${hexToRgba(statusColor, 0.35)}`,
                  }
                : {}
            }
          >
            {thread.status}
          </Tag>
        )}
      </button>
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison: only re-render if thread data actually changed
    return (
      prevProps.thread.id === nextProps.thread.id &&
      prevProps.thread.subject === nextProps.thread.subject &&
      prevProps.thread.requester === nextProps.thread.requester &&
      prevProps.thread.companyName === nextProps.thread.companyName &&
      prevProps.thread.status === nextProps.thread.status &&
      prevProps.thread.createdAt === nextProps.thread.createdAt &&
      prevProps.isUpdated === nextProps.isUpdated &&
      prevProps.thread.unreadCount === nextProps.thread.unreadCount
    );
  },
);
/* eslint-enable react/prop-types */

ChatThreadItem.displayName = "ChatThreadItem";

interface UnreadThreadsListProps {
  searchValue: string;
  setSearchValue: (value: string) => void;
  chatThreads: ChatThreadDisplay[];
  threadsLoading: boolean;
  loadingMore: boolean;
  isAdmin: boolean;
  onCreateChatClick: () => void;
  onThreadClick: (thread: ChatThreadDisplay) => void;
  statusOptions: any[];
  itemsContainerRef: React.RefObject<HTMLDivElement>;
  observerTargetRef: React.RefObject<HTMLDivElement>;
}

export const UnreadThreadsList: FC<UnreadThreadsListProps> = ({
  searchValue,
  setSearchValue,
  chatThreads,
  threadsLoading,
  loadingMore,
  itemsContainerRef,
  observerTargetRef,
  isAdmin,
  onCreateChatClick,
  onThreadClick,
  statusOptions,
}) => {
  return (
    <div className="chat-list">
      <div className="chat-list__actions">
        {!isAdmin && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={onCreateChatClick}
          >
            新規行作成（フォーム）
          </Button>
        )}
      </div>
      {/* <Input.Search
        placeholder="検索内容をご入力ください"
        value={searchValue}
        onChange={(e) => setSearchValue(e.target.value)}
        allowClear
        className="chat-list__search"
      /> */}

      <div className="chat-list__items" ref={itemsContainerRef}>
        {threadsLoading ? (
          // Skeleton loading placeholders
          Array.from({ length: 3 }).map((_, index) => (
            <div key={`skeleton-${index}`} className="chat-list__item-skeleton">
              <Skeleton
                active
                paragraph={{ rows: 2, width: ["100%", "60%"] }}
                title={{ width: "40%" }}
              />
            </div>
          ))
        ) : chatThreads.length === 0 ? (
          <div className="chat-list__empty">該当データがありません</div>
        ) : (
          <>
            {chatThreads.map((thread) => {
              return (
                <ChatThreadItem
                  key={thread.id}
                  thread={thread}
                  isUpdated={thread.isUpdated}
                  onClick={() => onThreadClick(thread)}
                  statusOptions={statusOptions}
                />
              );
            })}
            <div ref={observerTargetRef} style={{ height: "1px" }} />
            {loadingMore && (
              <div className="chat-list__loading-more">
                <Skeleton
                  active
                  paragraph={{ rows: 1, width: ["100%", "60%"] }}
                  title={{ width: "40%" }}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
